/**
 * Target Selector Component
 *
 * Picker for assignment targeting — Tags, Groups, Professional Roles,
 * and/or individual Users. Consolidated into a single tabbed surface
 * so admins pick the targeting TYPE first and only see the relevant
 * search field below (instead of four stacked autocompletes most of
 * which they ignore).
 *
 * Selections accumulate ACROSS tabs: switching from Groups to Roles
 * doesn't discard the groups you picked — it just hides that field
 * while you work on roles. Each tab carries a count badge so the
 * admin can see at a glance how many targets they've already accrued
 * across types.
 *
 * Individual-user targeting lives on the Users tab. It was hidden
 * earlier when bulk targeting (tags/groups/roles) was the only
 * intended path here — admins are putting it back because picking a
 * specific learner off the content row is the fastest way to handle
 * one-offs without bouncing to the user's profile page first.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Autocomplete,
  Box,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '../ui';
import { Group, Login, Tag, TagType, UserRole } from '../../apis/api-client';
import { groupsClient, tagsClient, usersClient } from '../../apis/client-instances';
import { RootState } from '../../types';
import { isAccountOrSystemAdmin } from '../../utils/roleHelpers';
import GroupAutocomplete from '../common/GroupAutocomplete';
import TagAutocomplete from '../common/TagAutocomplete';
import UserAutocomplete from '../common/UserAutocomplete';
import { Profession } from '../../apis/api-client';
import {
  selectAllProfessions,
  selectProfessionEntities,
  useEnsureProfessions,
} from '../../features/lookups/lookupsSlice';

type TargetTab = 'tags' | 'groups' | 'roles' | 'users';

interface TargetSelectorProps {
  selectedTagIds: number[];
  selectedGroupIds: number[];
  selectedProfessionIds: number[];
  selectedUserIds: number[];
  onChange: (
    tagIds: number[],
    groupIds: number[],
    professionIds: number[],
    userIds: number[],
  ) => void;
  error?: string;
}

export default function TargetSelector({
  selectedTagIds,
  selectedGroupIds,
  selectedProfessionIds,
  selectedUserIds,
  onChange,
  error,
}: TargetSelectorProps) {
  useEnsureProfessions();
  const professions = useSelector(selectAllProfessions);
  const professionEntities = useSelector(selectProfessionEntities);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Login[]>([]);

  const currentUser = useSelector((state: RootState) => state.profile.user);
  const isWorkspaceAdmin = currentUser?.role === UserRole.WorkspaceAdmin;
  const currentUserWorkspaceId = currentUser?.workspaceId;
  // Tag list is admin-only on the server; hide the Tags tab for
  // non-admins entirely.
  const canSeeTags = isAccountOrSystemAdmin(currentUser?.role);

  // Tabs available to this user — drives the toggle row and which tab
  // we default to on first open.
  const availableTabs: TargetTab[] = useMemo(() => {
    const tabs: TargetTab[] = [];
    if (canSeeTags) tabs.push('tags');
    tabs.push('groups');
    tabs.push('roles');
    tabs.push('users');
    return tabs;
  }, [canSeeTags]);

  // Default tab — pick the one that already has a selection so an
  // admin re-opening an existing assignment lands on the active facet.
  // Fall back to Groups (most common) for a fresh form.
  const [activeTab, setActiveTab] = useState<TargetTab>(() => {
    if (canSeeTags && selectedTagIds.length > 0) return 'tags';
    if (selectedGroupIds.length > 0) return 'groups';
    if (selectedProfessionIds.length > 0) return 'roles';
    if (selectedUserIds.length > 0) return 'users';
    return 'groups';
  });

  // Hydrate the tag chips from selectedTagIds (edit mode).
  // WorkspaceAdmins are forced workspace-scoped (also gated by canSeeTags
  // above, but kept defensive for future role-shape changes).
  useEffect(() => {
    if (!canSeeTags || selectedTagIds.length === 0) {
      setSelectedTags([]);
      return;
    }
    let cancelled = false;
    tagsClient
      .searchTags(
        undefined,
        TagType.UserTag,
        isWorkspaceAdmin ? currentUserWorkspaceId ?? undefined : undefined,
        isWorkspaceAdmin,
        false,
        selectedTagIds,
        1,
        selectedTagIds.length
      )
      .then((res) => {
        if (!cancelled) setSelectedTags(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setSelectedTags([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTagIds, canSeeTags, isWorkspaceAdmin, currentUserWorkspaceId]);

  // Hydrate the group multi-select from selectedGroupIds prop.
  useEffect(() => {
    if (selectedGroupIds.length === 0) {
      setSelectedGroups([]);
      return;
    }
    let cancelled = false;
    groupsClient
      .searchGroups(undefined, undefined, 1, selectedGroupIds.length, selectedGroupIds.map((n) => n))
      .then((res) => {
        if (!cancelled) setSelectedGroups(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setSelectedGroups([]);
      });
    return () => { cancelled = true; };
  }, [selectedGroupIds]);

  // Hydrate the user chips from selectedUserIds (edit mode + re-opens).
  // Same pattern as the group hydrator — single bulk fetch with the
  // `ids` filter so we don't fan out N requests.
  useEffect(() => {
    if (selectedUserIds.length === 0) {
      setSelectedUsers([]);
      return;
    }
    let cancelled = false;
    usersClient
      .searchUsersPaged(
        undefined, // workspaceId — server scopes by caller's role
        undefined, // role
        undefined, // status
        undefined, // search
        1,
        selectedUserIds.length,
        selectedUserIds,
      )
      .then((res) => {
        if (!cancelled) setSelectedUsers(res.items ?? []);
      })
      .catch(() => {
        if (!cancelled) setSelectedUsers([]);
      });
    return () => { cancelled = true; };
  }, [selectedUserIds]);

  const handleTagChange = (newValue: Tag[]) => {
    setSelectedTags(newValue);
    const tagIds = newValue.map((tag) => tag.id).filter((id): id is number => id !== undefined);
    onChange(tagIds, selectedGroupIds, selectedProfessionIds, selectedUserIds);
  };

  const handleGroupChange = (newValue: Group[]) => {
    setSelectedGroups(newValue);
    const groupIds = newValue.map((g) => g.id).filter((id): id is number => id !== undefined);
    onChange(selectedTagIds, groupIds, selectedProfessionIds, selectedUserIds);
  };

  const handleRoleChange = (newValue: Profession[]) => {
    const ids = newValue.map((p) => p.id).filter((id): id is number => id !== undefined);
    onChange(selectedTagIds, selectedGroupIds, ids, selectedUserIds);
  };

  const handleUserChange = (newValue: Login[]) => {
    setSelectedUsers(newValue);
    const userIds = newValue.map((u) => u.id).filter((id): id is number => id !== undefined);
    onChange(selectedTagIds, selectedGroupIds, selectedProfessionIds, userIds);
  };

  const groupWorkspaceFilter = isWorkspaceAdmin ? currentUserWorkspaceId ?? undefined : undefined;

  const counts: Record<TargetTab, number> = {
    tags: selectedTagIds.length,
    groups: selectedGroupIds.length,
    roles: selectedProfessionIds.length,
    users: selectedUserIds.length,
  };

  const tabLabel = (tab: TargetTab): string => {
    switch (tab) {
      case 'tags':
        return 'User Tags';
      case 'groups':
        return 'Groups';
      case 'roles':
        return 'Professional Roles';
      case 'users':
        return 'Users';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'}>
        Pick a targeting type, then search. You can mix types — switching tabs keeps the selections you've already made.
      </Typography>

      {/* Type picker — single source for "what kind of targeting am
          I editing right now?". Count badges on inactive tabs surface
          accrued selections so they don't feel hidden. */}
      <ToggleButtonGroup
        value={activeTab}
        exclusive
        size="small"
        onChange={(_e, next: TargetTab | null) => {
          if (next) setActiveTab(next);
        }}
        sx={{ alignSelf: 'flex-start' }}
      >
        {availableTabs.map((tab) => {
          const n = counts[tab];
          return (
            <ToggleButton key={tab} value={tab} sx={{ textTransform: 'none', px: 2 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                {tabLabel(tab)}
                {n > 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 18,
                      height: 18,
                      px: 0.5,
                      borderRadius: 999,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      bgcolor: 'primary.main',
                      color: '#fff',
                    }}
                  >
                    {n}
                  </Box>
                )}
              </Box>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>

      {/* Active tab's search field. Only one of these is mounted at a
          time so the dialog stays tight. */}
      {activeTab === 'tags' && canSeeTags && (
        <TagAutocomplete
          multiple
          value={selectedTags}
          onChange={(v) => handleTagChange((v as Tag[]) ?? [])}
          tagType={TagType.UserTag}
          workspaceId={isWorkspaceAdmin ? currentUserWorkspaceId ?? undefined : undefined}
          includeAccountWide={isWorkspaceAdmin}
          size="medium"
          fullWidth
          label="Search user tags"
          placeholder="Type to search…"
          error={!!error}
        />
      )}

      {activeTab === 'groups' && (
        <GroupAutocomplete
          multiple
          value={selectedGroups}
          onChange={(v) => handleGroupChange((v as Group[]) ?? [])}
          workspaceId={groupWorkspaceFilter}
          size="medium"
          fullWidth
          label="Search groups"
          placeholder="Type to search…"
          error={!!error}
        />
      )}

      {activeTab === 'users' && (
        <UserAutocomplete
          multiple
          value={selectedUsers}
          onChange={(v) => handleUserChange((v as Login[]) ?? [])}
          // Workspace admins are already server-scoped to their tenant,
          // but pinning the filter here keeps the dropdown honest if a
          // future role gains broader read access.
          workspaceId={isWorkspaceAdmin ? currentUserWorkspaceId ?? undefined : undefined}
          size="medium"
          fullWidth
          label="Search users"
          placeholder="Type a name or email…"
          error={!!error}
        />
      )}

      {activeTab === 'roles' && (
        <Autocomplete<Profession, true, false, false>
          multiple
          size="medium"
          fullWidth
          options={professions}
          value={selectedProfessionIds
            .map((id) => professionEntities[id])
            .filter((p): p is Profession => !!p)}
          onChange={(_e, v) => handleRoleChange(v as Profession[])}
          getOptionLabel={(p) => p.name ?? ''}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          disableCloseOnSelect
          renderTags={(items, getTagProps) =>
            items.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name ?? ''}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pick professions"
              placeholder="Choose one or more…"
              error={!!error}
            />
          )}
        />
      )}

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Box>
  );
}
